import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from '../ui/use-toast';
import { useAuth } from '../../contexts/PhpAuthContext';
import { useData } from '../../contexts/DataContext';
import { apiRequest } from '../../config/api';
import { Calendar, Clock, User, Phone, Stethoscope, Filter, CheckCircle, XCircle, AlertCircle, CalendarCheck, Edit, FileText, Printer, Download, Eye, Mail, Search, ChevronLeft, ChevronRight, X, Info } from 'lucide-react';
import { pdf } from '@react-pdf/renderer';
import PatientRecordPDF from '../pdf/PatientRecordPDF';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import PatientFormsWizard from './PatientFormsWizard';
import ReviewSubmitPage from './ReviewSubmitPage';

const AppointmentsTab = ({ viewAsAdmin = false }) => {
  const { profile } = useAuth();
  const { patients, fetchPatients } = useData();
  const [allAppointments, setAllAppointments] = useState([]); // Store all appointments
  const [loading, setLoading] = useState(true);
  const [doctors, setDoctors] = useState([]);
  const [doctorFilter, setDoctorFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState(''); // Search by patient name or doctor name
  const [selectedPatientForBooking, setSelectedPatientForBooking] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [appointmentData, setAppointmentData] = useState({
    patient_name: '',
    phone: '',
    date: '',
    time_slot: '',
    service_type: 'Checkup',
    status: 'Pending'
  });
  const [bookedSlots, setBookedSlots] = useState([]);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    appointment_date: '',
    time_slot: '',
    status: 'Scheduled',
    service_type: '',
    notes: ''
  });
  const [editBookedSlots, setEditBookedSlots] = useState([]);
  const [showMedicalHistory, setShowMedicalHistory] = useState(false);
  const [selectedPatientForHistory, setSelectedPatientForHistory] = useState(null);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState(null);
  const [showViewPDF, setShowViewPDF] = useState(false);
  const [selectedAppointmentForPDF, setSelectedAppointmentForPDF] = useState(null);
  const [submissionData, setSubmissionData] = useState(null);
  const [loadingSubmissionData, setLoadingSubmissionData] = useState(false);
  const [viewingPdfUrl, setViewingPdfUrl] = useState(null); // PDF URL for modal display
  
  // Detect mobile device
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const [viewMode, setViewMode] = useState('detailed'); // 'detailed' or 'list'
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Reset to first page when view mode or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [viewMode, doctorFilter, searchTerm]);

  useEffect(() => {
    // Only fetch if user is logged in
    if (!profile) {
      setLoading(false);
      return;
    }
    
    fetchAppointments();
    if (profile?.role === 'admin' || (profile?.role === 'chief_doctor' && viewAsAdmin)) {
      fetchDoctors();
    }
    
    // Fetch patients for booking dropdown (for doctors, chief_doctor, and clinic_login)
    if (!viewAsAdmin && (profile?.role === 'doctor' || profile?.role === 'chief_doctor' || profile?.role === 'clinic_login')) {
      // Fetch only their own patients if they don't have permission to view others
      if (profile.role === 'clinic_login' || !profile.can_view_other_patients) {
        fetchPatients(profile.id);
      } else {
        fetchPatients();
      }
    }
  }, [profile, doctorFilter, viewAsAdmin, fetchPatients]);
  
  // Load booked slots when booking modal opens and date is selected
  useEffect(() => {
    if (showBookingModal && appointmentData.date && profile) {
      loadBookedSlots(appointmentData.date);
    }
  }, [showBookingModal, appointmentData.date, profile]);

  // Load booked slots when edit modal opens and date is selected
  useEffect(() => {
    if (showEditModal && editFormData.appointment_date && profile) {
      // Exclude the current appointment's time slot from booked slots
      const currentAppointmentTime = editingAppointment?.appointment_time;
      loadBookedSlots(editFormData.appointment_date, null, currentAppointmentTime).then(slots => {
        setEditBookedSlots(slots);
      });
    } else if (showEditModal && !editFormData.appointment_date) {
      setEditBookedSlots([]);
    }
  }, [showEditModal, editFormData.appointment_date, profile, editingAppointment]);
  
  // Filter appointments based on search term and filters
  const appointments = useMemo(() => {
    let filtered = [...allAppointments];
    
    // Only show "Scheduled" appointments in Appointments tab (exclude "Submitted")
    filtered = filtered.filter(apt => apt.status === 'Scheduled');
    
    // Filter by search term (patient name or doctor name)
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(apt => {
        const patientName = (apt.patient_name || apt.patient?.name || '').toLowerCase();
        const doctorName = (apt.doctor_name || apt.doctor?.name || apt.assigned_doctor_name || '').toLowerCase();
        return patientName.includes(searchLower) || doctorName.includes(searchLower);
      });
    }
    
    return filtered;
  }, [allAppointments, searchTerm]);

  // Listen for appointment submission events to refresh the list
  useEffect(() => {
    const handleAppointmentSubmitted = () => {
      fetchAppointments();
    };
    
    window.addEventListener('appointment-submitted', handleAppointmentSubmitted);
    return () => {
      window.removeEventListener('appointment-submitted', handleAppointmentSubmitted);
    };
  }, []);

  // Get patients for the doctor/clinic_login (only their own patients when not viewing as admin)
  // For admin, show all patients
  const doctorPatients = useMemo(() => {
    if (!profile) return [];
    
    // Admin or viewing as admin - show all patients
    if (profile.role === 'admin' || viewAsAdmin) {
      return patients;
    }
    
    if (profile.role === 'doctor' || profile.role === 'chief_doctor' || profile.role === 'clinic_login') {
      // Filter patients by created_by for doctor/clinic_login view
      // clinic_login always sees only their own patients
      if (profile.role === 'clinic_login' || !profile.can_view_other_patients) {
        return patients.filter(p => p.created_by === profile.id);
      }
    }
    
    return patients;
  }, [patients, profile, viewAsAdmin]);

  const fetchDoctors = async () => {
    try {
      const data = await apiRequest('get_users.php?role=doctor');
      if (data.success) {
        setDoctors(data.users || []);
      }
    } catch (error) {
      console.error('Error fetching doctors:', error);
    }
  };

  const fetchAppointments = async () => {
    setLoading(true);
    try {
      let url = 'get_appointments_list.php';
      const params = new URLSearchParams();
      
      // Add doctor filter if not admin
      // If viewAsAdmin is true for chief_doctor, treat like admin (no filter)
      if (!viewAsAdmin && profile?.role === 'doctor' && !profile?.can_view_other_patients) {
        params.append('doctor_id', profile.id);
      } else if (!viewAsAdmin && profile?.role === 'chief_doctor') {
        // Chief doctor viewing as doctor - only show their appointments
        params.append('doctor_id', profile.id);
      } else if (!viewAsAdmin && profile?.role === 'clinic_login') {
        // Clinic login - only show appointments for their patients
        params.append('doctor_id', profile.id);
      }
      
      if (params.toString()) {
        url += '?' + params.toString();
      }
      
      const data = await apiRequest(url);
      if (data.success) {
        let filteredAppointments = data.appointments || [];
        
        setAllAppointments(filteredAppointments);
      } else {
        // Check if it's an authentication error
        if (data.error === 'Unauthorized' || data.message?.includes('Session token')) {
          toast({
            title: 'Session Expired',
            description: 'Please log out and log in again to continue.',
            variant: 'destructive'
          });
          // Optionally redirect to login after a delay
          setTimeout(() => {
            window.location.href = '/';
          }, 2000);
        } else {
          toast({
            title: 'Error',
            description: data.error || data.message || 'Failed to fetch appointments',
            variant: 'destructive'
          });
        }
      }
    } catch (error) {
      console.error('Error fetching appointments:', error);
      
      // Check if error message indicates authentication issue
      if (error.message?.includes('Unauthorized') || error.message?.includes('Session token')) {
        toast({
          title: 'Session Expired',
          description: 'Please log out and log in again to continue.',
          variant: 'destructive'
        });
      } else {
        toast({
          title: 'Error',
          description: error.message || 'Failed to fetch appointments',
          variant: 'destructive'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Scheduled':
      case 'Confirmed':
        return <CalendarCheck className="h-4 w-4 text-blue-400" />;
      case 'Completed':
        return <CheckCircle className="h-4 w-4 text-green-400" />;
      case 'Cancelled':
        return <XCircle className="h-4 w-4 text-red-400" />;
      case 'No Show':
        return <AlertCircle className="h-4 w-4 text-yellow-400" />;
      default:
        return <Calendar className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'Scheduled':
      case 'Confirmed':
      case 'Upcoming':
        return 'bg-blue-500/20 text-blue-400';
      case 'Submitted':
        return 'bg-purple-500/20 text-purple-400';
      case 'Completed':
        return 'bg-green-500/20 text-green-400';
      case 'Cancelled':
        return 'bg-red-500/20 text-red-400';
      case 'No Show':
        return 'bg-yellow-500/20 text-yellow-400';
      default:
        return 'bg-gray-500/20 text-gray-400';
    }
  };

  const getTimeStatusBadge = (timeStatus) => {
    switch (timeStatus) {
      case 'past':
        return <span className="px-2 py-1 rounded text-xs bg-gray-500/20 text-gray-400">Past</span>;
      case 'current':
        return <span className="px-2 py-1 rounded text-xs bg-orange-500/20 text-orange-400">Current</span>;
      case 'future':
        return <span className="px-2 py-1 rounded text-xs bg-green-500/20 text-green-400">Upcoming</span>;
      default:
        return null;
    }
  };

  // Get submitted date status (Today or Past) for submitted appointments
  const getSubmittedDateStatus = (appointmentDate) => {
    if (!appointmentDate) return null;
    
    const submittedDate = new Date(appointmentDate);
    const today = new Date();
    
    // Reset time to compare only dates
    submittedDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    
    if (submittedDate.getTime() === today.getTime()) {
      return 'today';
    } else if (submittedDate < today) {
      return 'past';
    }
    return null;
  };

  // Render submitted date badge (Today/Past)
  const getSubmittedDateBadge = (appointmentDate) => {
    const status = getSubmittedDateStatus(appointmentDate);
    if (!status) return null;
    
    if (status === 'today') {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400">Today</span>;
    } else if (status === 'past') {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-500/20 text-gray-400">Past</span>;
    }
    return null;
  };

  const formatDateTime = (date, time) => {
    const dateObj = new Date(date + 'T' + time);
    return {
      date: dateObj.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' }),
      time: dateObj.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    };
  };

  const handleEditAppointment = (appointment) => {
    // Convert appointment_time to time_slot format (HH:MM:SS)
    let timeSlot = appointment.appointment_time || '';
    if (timeSlot && timeSlot.length === 5) {
      timeSlot = timeSlot + ':00';
    }
    
    setEditFormData({
      appointment_date: appointment.appointment_date,
      time_slot: timeSlot,
      status: appointment.status,
      service_type: appointment.service_type || '',
      notes: appointment.notes || ''
    });
    setEditingAppointment(appointment);
    setShowEditModal(true);
  };

  const handleUpdateAppointment = async () => {
    try {
      // Convert time_slot back to appointment_time format for API
      const updateData = {
        ...editFormData,
        appointment_time: editFormData.time_slot
      };
      delete updateData.time_slot; // Remove time_slot, use appointment_time
      
      const data = await apiRequest('update_appointment.php', {
        method: 'PUT',
        body: JSON.stringify({
          id: editingAppointment.id,
          ...updateData
        })
      });

      if (data.success) {
        toast({
          title: 'Success',
          description: 'Appointment updated successfully',
          variant: 'default'
        });
        setShowEditModal(false);
        setEditingAppointment(null);
        fetchAppointments();
      } else {
        toast({
          title: 'Error',
          description: data.error || 'Failed to update appointment',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update appointment',
        variant: 'destructive'
      });
    }
  };

  const handleViewMedicalHistory = (appointment) => {
    setSelectedPatientForHistory({
      id: appointment.patient_id,
      name: appointment.patient_name
    });
    setSelectedAppointmentId(appointment.id); // Store the appointment ID
    setShowMedicalHistory(true);
  };

  const handleViewPDF = async (appointment) => {
    try {
      // First, find the submission linked to this appointment and check for uploaded PDF URL
      const submissionsResponse = await apiRequest('get_patient_record_submissions.php');
      if (submissionsResponse.success && submissionsResponse.submissions) {
        const submission = submissionsResponse.submissions.find(
          sub => sub.appointment_id === appointment.id
        );
        
        // If submission exists and has PDF URL, show PDF in modal
        if (submission && submission.pdf_url) {
          // Convert relative URL to absolute if needed
          const absoluteUrl = submission.pdf_url.startsWith('http') 
            ? submission.pdf_url 
            : `${window.location.origin}${submission.pdf_url.startsWith('/') ? '' : '/'}${submission.pdf_url}`;
          
          // Store PDF URL and show modal
          setViewingPdfUrl(absoluteUrl);
          setSelectedAppointmentForPDF({
            patientId: appointment.patient_id,
            patientName: appointment.patient_name,
            appointmentId: appointment.id,
            appointmentReferenceId: appointment.appointment_reference_id
          });
          setShowViewPDF(true);
          return; // Exit early - PDF will be shown in modal
        }
      }
      
      // Fallback: If no PDF URL exists, open modal with ReviewSubmitPage (generate/view data)
      setViewingPdfUrl(null); // Clear PDF URL
      setSelectedAppointmentForPDF({
        patientId: appointment.patient_id,
        patientName: appointment.patient_name,
        appointmentId: appointment.id,
        appointmentReferenceId: appointment.appointment_reference_id
      });
      setLoadingSubmissionData(true);
      setSubmissionData(null);
      setShowViewPDF(true);
      
      try {
        // Fetch all submissions and find the one linked to this appointment
        const submissionsResponse = await apiRequest('get_patient_record_submissions.php');
        if (submissionsResponse.success && submissionsResponse.submissions) {
          // Find submission linked to this appointment
          const submission = submissionsResponse.submissions.find(
            sub => sub.appointment_id === appointment.id
          );
          
          if (submission) {
            // Fetch full submission data
            const data = await apiRequest(`get_patient_record_submission.php?submission_id=${submission.id}`);
            if (data.success) {
              // Transform procedures data to match expected format
              const transformedData = {
                ...data,
                appointment_id: submission.appointment_id,
                appointment_reference_id: submission.appointment_reference_id || appointment.appointment_reference_id,
                patient_phone: submission.profiles?.phone || null,
                patient_address: submission.profiles?.address || null,
                procedures: Array.isArray(data.procedures) 
                  ? { rows: data.procedures }
                  : (data.procedures || null)
              };
              setSubmissionData(transformedData);
            }
          } else {
            console.log('No submission found for appointment:', appointment.id);
          }
        }
      } catch (error) {
        console.error('Error fetching submission data:', error);
        // Still show the modal, ReviewSubmitPage will fetch data itself
      } finally {
        setLoadingSubmissionData(false);
      }
    } catch (error) {
      console.error('Error viewing PDF:', error);
      toast({
        title: 'Error',
        description: 'Failed to open PDF',
        variant: 'destructive'
      });
    }
  };

  // Handle booking appointment
  const handleBookAppointmentClick = () => {
    if (!selectedPatientForBooking) {
      toast({
        title: 'Error',
        description: 'Please select a patient first',
        variant: 'destructive'
      });
      return;
    }
    
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const defaultDate = tomorrow.toISOString().split('T')[0];
    
    setAppointmentData({
      patient_name: selectedPatientForBooking.full_name || selectedPatientForBooking.name || '',
      phone: selectedPatientForBooking.phone || '',
      date: defaultDate,
      time_slot: '',
      service_type: 'Checkup',
      status: 'Pending'
    });
    setShowBookingModal(true);
    loadBookedSlots(defaultDate);
  };

  // Load booked time slots for a specific date
  const loadBookedSlots = async (date, doctorId = null, excludeTimeSlot = null) => {
    if (!date || !profile) return [];
    
    // For admin or chief_doctor viewing as admin, use selected doctor or first available doctor
    const targetDoctorId = doctorId || ((profile.role === 'admin' || (profile.role === 'chief_doctor' && viewAsAdmin)) 
      ? (doctors.length > 0 ? doctors[0].id : null) 
      : profile.id);
    if (!targetDoctorId) return [];
    
    try {
      const data = await apiRequest(`get_appointments.php?date=${date}&doctor_id=${targetDoctorId}`);
      if (data.success) {
        // Normalize time format (handle both HH:MM:SS and HH:MM)
        let booked = data.appointments.map(apt => {
          const time = apt.appointment_time;
          // If time is in HH:MM format, add :00
          if (time && time.length === 5) {
            return time + ':00';
          }
          return time;
        });
        
        // Exclude the current appointment's time slot if provided
        if (excludeTimeSlot) {
          const excludeTime = excludeTimeSlot.length === 5 ? excludeTimeSlot + ':00' : excludeTimeSlot;
          booked = booked.filter(time => time !== excludeTime);
        }
        
        setBookedSlots(booked);
        return booked;
      }
    } catch (error) {
      console.error('Error loading booked slots:', error);
      setBookedSlots([]);
    }
    return [];
  };

  // Handle date change
  const handleBookingDateChange = (date) => {
    setAppointmentData({ ...appointmentData, date, time_slot: '' });
    loadBookedSlots(date);
  };

  // Generate time slots based on day of week (1-hour intervals)
  // Monday-Saturday: 9:30 AM to 9:00 PM (1-hour intervals)
  // Sunday: 10:00 AM to 7:00 PM (1-hour intervals)
  const generateTimeSlots = (date = null, bookedSlotsList = null) => {
    const targetDate = date || appointmentData.date;
    if (!targetDate) return [];
    
    const slotsToCheck = bookedSlotsList !== null ? bookedSlotsList : bookedSlots;
    const dateObj = new Date(targetDate + 'T00:00:00'); // Add time to avoid timezone issues
    const dayOfWeek = dateObj.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    const slots = [];
    
    // Generate 1-hour intervals based on day
    if (dayOfWeek === 0) {
      // Sunday: 10:00 AM to 7:00 PM (hourly on the hour)
      for (let hour = 10; hour <= 19; hour++) {
        const time = `${hour.toString().padStart(2, '0')}:00:00`;
        const displayTime = new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
        const isBooked = slotsToCheck.includes(time);
        slots.push({
          value: time,
          label: displayTime,
          disabled: isBooked
        });
      }
    } else {
      // Monday-Saturday: 9:30 AM to 9:00 PM
      // First slot: 9:30 AM
      const firstTime = '09:30:00';
      slots.push({
        value: firstTime,
        label: new Date('2000-01-01T09:30:00').toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        }),
        disabled: slotsToCheck.includes(firstTime)
      });
      
      // Then hourly slots at :30 minutes: 10:30, 11:30, ..., 8:30
      for (let hour = 10; hour <= 20; hour++) {
        const time = `${hour.toString().padStart(2, '0')}:30:00`;
        const displayTime = new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
        const isBooked = slotsToCheck.includes(time);
        slots.push({
          value: time,
          label: displayTime,
          disabled: isBooked
        });
      }
      
      // Final slot: 9:00 PM
      const lastTime = '21:00:00';
      slots.push({
        value: lastTime,
        label: new Date('2000-01-01T21:00:00').toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        }),
        disabled: slotsToCheck.includes(lastTime)
      });
    }
    
    return slots;
  };
  
  // Get available slots count
  const getAvailableSlotsCount = (date = null, bookedSlotsList = null) => {
    const allSlots = generateTimeSlots(date, bookedSlotsList);
    const available = allSlots.filter(slot => !slot.disabled);
    return available.length;
  };

  // Handle appointment creation
  const handleCreateBooking = async () => {
    if (!selectedPatientForBooking || !profile) return;

    // Validate required fields
    if (!appointmentData.date || !appointmentData.time_slot) {
      toast({
        title: 'Error',
        description: 'Please select date and time slot',
        variant: 'destructive'
      });
      return;
    }

    try {
      // For admin or chief_doctor viewing as admin, use first available doctor
      // For doctors, use profile.id
      const targetDoctorId = (profile.role === 'admin' || (profile.role === 'chief_doctor' && viewAsAdmin))
        ? (doctors.length > 0 ? doctors[0].id : profile.id)
        : profile.id;
      
      const data = await apiRequest('add_appointment.php', {
        method: 'POST',
        body: JSON.stringify({
          patient_id: selectedPatientForBooking.id,
          doctor_id: targetDoctorId,
          appointment_date: appointmentData.date,
          appointment_time: appointmentData.time_slot,
          service_type: appointmentData.service_type,
          status: appointmentData.status || 'Pending'
        })
      });

      if (data.success) {
        toast({
          title: 'Success',
          description: 'Appointment booked successfully'
        });
        setShowBookingModal(false);
        setSelectedPatientForBooking(null);
        setAppointmentData({
          patient_name: '',
          phone: '',
          date: '',
          time_slot: '',
          service_type: 'Checkup',
          status: 'Pending'
        });
        setBookedSlots([]);
        // Refresh appointments list
        fetchAppointments();
        // Trigger appointment-created event
        window.dispatchEvent(new CustomEvent('appointment-created'));
      } else {
        toast({
          title: 'Error',
          description: data.error || data.message || 'Failed to book appointment',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to book appointment',
        variant: 'destructive'
      });
    }
  };

  const handleDownloadPDF = () => {
    if (viewingPdfUrl) {
      // Extract filename from URL to maintain consistency with uploaded filename
      let filename = 'Patient-Record.pdf'; // Default fallback
      
      try {
        // Extract filename from URL path
        const urlPath = viewingPdfUrl.split('?')[0]; // Remove query parameters
        const pathParts = urlPath.split('/');
        const urlFilename = pathParts[pathParts.length - 1];
        
        // If filename exists in URL, use it; otherwise use default
        if (urlFilename && urlFilename.endsWith('.pdf')) {
          filename = urlFilename;
        } else {
          // Fallback: construct from appointment data
          filename = `Patient-Record-${selectedAppointmentForPDF?.patientName || 'Record'}-${selectedAppointmentForPDF?.appointmentReferenceId || 'N/A'}.pdf`;
        }
      } catch (error) {
        console.error('Error extracting filename from URL:', error);
        // Fallback: construct from appointment data
        filename = `Patient-Record-${selectedAppointmentForPDF?.patientName || 'Record'}-${selectedAppointmentForPDF?.appointmentReferenceId || 'N/A'}.pdf`;
      }
      
      const link = document.createElement('a');
      link.href = viewingPdfUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast({
        title: 'Download started',
        description: 'Your PDF is being downloaded',
      });
    }
  };

  const handlePrintAppointment = async (appointment) => {
    try {
      // First, find the submission linked to this appointment
      const submissionsResponse = await apiRequest('get_patient_record_submissions.php');
      if (submissionsResponse.success && submissionsResponse.submissions) {
        const submission = submissionsResponse.submissions.find(
          sub => sub.appointment_id === appointment.id
        );
        
        if (submission && submission.pdf_url) {
          // Open PDF URL in new window/tab for printing
          // Convert relative URL to absolute if needed
          const absoluteUrl = submission.pdf_url.startsWith('http') 
            ? submission.pdf_url 
            : `${window.location.origin}${submission.pdf_url.startsWith('/') ? '' : '/'}${submission.pdf_url}`;
          
          const printWindow = window.open(absoluteUrl, '_blank');
          
          if (printWindow) {
            // Wait for PDF to load, then trigger print dialog
            printWindow.onload = () => {
              setTimeout(() => {
                printWindow.print();
              }, 500);
            };
          } else {
            toast({
              title: 'Error',
              description: 'Please allow pop-ups to print the PDF',
              variant: 'destructive'
            });
          }
        } else {
          // If no PDF URL exists, fall back to opening view modal and printing
          await handleViewPDF(appointment);
          setTimeout(() => {
            window.print();
          }, 500);
        }
      } else {
        // Fallback to view modal print
        await handleViewPDF(appointment);
        setTimeout(() => {
          window.print();
        }, 500);
      }
    } catch (error) {
      console.error('Error printing PDF:', error);
      toast({
        title: 'Error',
        description: 'Failed to open PDF for printing',
        variant: 'destructive'
      });
      // Fallback to view modal print
      await handleViewPDF(appointment);
      setTimeout(() => {
        window.print();
      }, 500);
    }
  };

  const handleEmailAppointment = async (appointment) => {
    try {
      // Show loading toast
      toast({
        title: 'Preparing email...',
        description: 'Please wait while we prepare your email.',
      });

      // First, find the submission linked to this appointment
      const submissionsResponse = await apiRequest('get_patient_record_submissions.php');
      if (submissionsResponse.success && submissionsResponse.submissions) {
        const submission = submissionsResponse.submissions.find(
          sub => sub.appointment_id === appointment.id
        );
        
        if (!submission) {
          toast({
            title: 'Error',
            description: 'No submission found for this appointment',
            variant: 'destructive'
          });
          return;
        }

        // Check if PDF URL already exists (uploaded during submission)
        let pdfUrl = submission.pdf_url || null;
        
        // If PDF doesn't exist, generate and upload it
        if (!pdfUrl) {
          toast({
            title: 'Generating PDF...',
            description: 'Creating PDF file for email.',
          });

          // Fetch full submission data
          const fullData = await apiRequest(`get_patient_record_submission.php?submission_id=${submission.id}`);
          if (!fullData.success) {
            throw new Error('Failed to load submission data');
          }

        // Generate filename - Standard format: Patient-Record-{PatientName}-{AppointmentReferenceID}-{Date}-{Timestamp}-{Random}.pdf
        // Add timestamp and random suffix to prevent overwrites
        const patientName = fullData?.patient_name || appointment.patient_name || 'Patient';
        const appointmentReferenceId = fullData?.appointment_reference_id || appointment.appointment_reference_id || appointment.id || 'Record';
        const date = new Date().toISOString().split('T')[0];
        const sanitizedPatientName = patientName.replace(/[^a-zA-Z0-9\s-]/g, '').replace(/\s+/g, '-');
        // Add high-precision timestamp (milliseconds + random) to ensure absolute uniqueness and prevent overwrites
        const timestamp = Date.now();
        const randomSuffix = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
        const uniqueId = `${timestamp}-${randomSuffix}`;
        const filename = `Patient-Record-${sanitizedPatientName}-${appointmentReferenceId}-${date}-${uniqueId}.pdf`;

        // Transform procedures data for PDF
        let proceduresForPDF = null;
        if (fullData.procedures) {
          if (Array.isArray(fullData.procedures)) {
            proceduresForPDF = { rows: fullData.procedures };
          } else if (fullData.procedures.rows || fullData.procedures.procedures) {
            proceduresForPDF = fullData.procedures;
          }
        }

        // Create PDF document
        const doc = (
          <PatientRecordPDF
            patientInfo={{
              name: fullData.patient_name || appointment.patient_name || 'N/A',
              phone: fullData.patient_phone || appointment.patient_phone || 'N/A',
              address: fullData.patient_address || 'N/A',
            }}
            appointmentDetails={{
              referenceId: fullData.appointment_reference_id || appointment.appointment_reference_id || appointment.id || 'N/A',
              id: appointment.id || 'N/A',
            }}
            medicalHistory={fullData.medical_history || null}
            dentalExamination={fullData.dental_examination || null}
            procedures={proceduresForPDF}
            profile={{}}
          />
        );

        // Generate PDF blob
        const blob = await pdf(doc).toBlob();

        // Upload PDF to server
        const formData = new FormData();
        formData.append('pdf', blob, filename);
        formData.append('submission_id', submission.id);
        formData.append('filename', filename);
        // Add patient name for folder organization
        if (patientName) {
          formData.append('patient_name', patientName);
        }

        const token = localStorage.getItem('session_token');
        const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://test.gugandentalcare.com/api/api';

        const uploadResponse = await fetch(`${API_BASE_URL}/upload_patient_record_pdf.php`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (!uploadResponse.ok) {
          const uploadError = await uploadResponse.json();
          throw new Error(uploadError.error || 'Failed to upload PDF');
        }

        const uploadData = await uploadResponse.json();
        pdfUrl = uploadData.url || uploadData.path;
        }

        // Send email with PDF link
        const emailData = await apiRequest('email_patient_record.php', {
          method: 'POST',
          body: JSON.stringify({ 
            submission_id: submission.id,
            pdf_url: pdfUrl
          })
        });
        
        if (emailData.success) {
          toast({
            title: 'Success',
            description: 'Patient record sent via email with PDF download link'
          });
        } else {
          toast({
            title: 'Error',
            description: emailData.error || 'Failed to send email',
            variant: 'destructive'
          });
        }
      } else {
        throw new Error('Failed to fetch submissions');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to send email',
        variant: 'destructive'
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-gray-400">Loading appointments...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Book Appointment */}
      <Card className="glass-effect border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center">
            <Calendar className="h-5 w-5 mr-2" />
            Book Appointment
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Informational Text */}
          <div className="bg-gradient-to-r from-green-600/30 via-green-500/30 to-green-600/30 border border-green-500/50 rounded-lg p-4 flex items-start gap-3 relative overflow-hidden mb-4">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-green-400/30 to-transparent animate-shimmer"></div>
            <Info className="h-5 w-5 text-green-300 flex-shrink-0 mt-0.5 relative z-10 animate-pulse" />
            <div className="flex-1 relative z-10">
              <p className="text-green-100 text-sm sm:text-base animate-fadeIn">
                {doctorPatients.length === 0 && !viewAsAdmin && (profile?.role === 'doctor' || profile?.role === 'chief_doctor' || profile?.role === 'clinic_login') 
                  ? "New patient : Please add the patient under the Patients tab, then book an appointment. After that, you can add the patient’s medical historyNew patient : Add the patient in the Patients tab, book an appointment, then add medical historyPlease Add Patient under patient tab then book an appointment and then add patient's medical history."
                  : "Existing patient : For existing patients, please book an appointment first to add the patient’s medical history."
                }
              </p>
            </div>
          </div>

          {/* Book Appointment Section - For Doctor Dashboard, Clinic Login Dashboard, and Admin Dashboard */}
          {(profile?.role === 'admin' || profile?.role === 'doctor' || profile?.role === 'chief_doctor' || profile?.role === 'clinic_login') && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <Label htmlFor="patient-select" className="text-white mb-2 block text-sm">Select Patient</Label>
                <Select 
                  value={selectedPatientForBooking?.id?.toString() || ''} 
                  onValueChange={(value) => {
                    if (value === 'no-patients') return; // Don't select the "no patients" option
                    const patient = doctorPatients.find(p => p.id.toString() === value);
                    setSelectedPatientForBooking(patient || null);
                  }}
                >
                  <SelectTrigger id="patient-select" className="bg-gray-800 border-gray-600 text-white">
                    <SelectValue placeholder="Select a patient" />
                  </SelectTrigger>
                  <SelectContent>
                    {doctorPatients.length === 0 ? (
                      <SelectItem value="no-patients" disabled>No patients available</SelectItem>
                    ) : (
                      doctorPatients.map((patient) => (
                        <SelectItem key={patient.id} value={patient.id.toString()}>
                          {patient.full_name || patient.name} {patient.patient_unique_id && `(${patient.patient_unique_id})`}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button
                  onClick={handleBookAppointmentClick}
                  disabled={!selectedPatientForBooking}
                  className="w-full medical-gradient text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Calendar className="h-4 w-4 mr-2" />
                  Book Appointment
                </Button>
              </div>
            </div>
          )}
          
        </CardContent>
      </Card>

      {/* Scheduled Appointments */}
      <Card className="glass-effect border-gray-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white flex items-center">
              <Calendar className="h-5 w-5 mr-2" />
              Scheduled Appointments
            </CardTitle>
            {/* View Toggle */}
            <div className="flex items-center gap-2">
              <Label htmlFor="view-mode-appointments" className="text-white text-sm cursor-pointer">
                List View
              </Label>
              <input
                type="checkbox"
                id="view-mode-appointments"
                checked={viewMode === 'list'}
                onChange={(e) => setViewMode(e.target.checked ? 'list' : 'detailed')}
                className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500 cursor-pointer"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search Box */}
          <div className="mb-4">
            <Label htmlFor="search-appointments" className="text-white mb-2 block">Search</Label>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                id="search-appointments"
                type="text"
                placeholder="Search by patient name or doctor name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8 bg-gray-800 border-gray-600 text-white placeholder-gray-400"
              />
            </div>
          </div>

          {/* Appointments List */}
          {appointments.length === 0 ? (
            <div className="p-8 text-center">
              <Calendar className="h-12 w-12 text-gray-500 mx-auto mb-4" />
              <p className="text-gray-400">No scheduled appointments found</p>
            </div>
          ) : viewMode === 'list' ? (
        /* List View with Pagination */
        <>
          <div className="space-y-3">
            {(() => {
              // Pagination calculations for list view
              const totalPages = Math.ceil(appointments.length / itemsPerPage);
              const startIndex = (currentPage - 1) * itemsPerPage;
              const endIndex = startIndex + itemsPerPage;
              const paginatedAppointments = appointments.slice(startIndex, endIndex);

              return paginatedAppointments.map((appointment) => {
            const { date, time } = formatDateTime(appointment.appointment_date, appointment.appointment_time);
            const isPatientBooked = appointment.creator_type === 'patient' || appointment.creator_patient_name;
            const getBookedByText = () => {
              if (!appointment.creator_name && !appointment.creator_patient_name) return null;
              const creatorName = appointment.creator_name || appointment.creator_patient_name;
              if (isPatientBooked) return creatorName;
              const isDoctor = appointment.creator_type === 'doctor' || 
                              appointment.creator_role === 'doctor' ||
                              appointment.creator_type === 'chief_doctor' ||
                              (appointment.creator_type === 'user' && appointment.creator_role === 'doctor');
              return isDoctor ? `Dr. ${creatorName}` : creatorName;
            };
            const bookedByText = getBookedByText();
            
            return (
              <div
                key={appointment.id}
                className={`p-3 bg-gray-800 rounded-lg flex items-center justify-between gap-2 sm:gap-3 flex-wrap ${
                  isPatientBooked ? 'border border-blue-500/50' : ''
                }`}
              >
                <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0 ${getStatusBadgeColor(appointment.status)}`}>
                    {appointment.status}
                  </span>
                  <Calendar className="h-4 w-4 text-orange-400 flex-shrink-0" />
                  <p className="text-white font-medium truncate text-sm sm:text-base">
                    {appointment.patient_name || 'N/A'}
                  </p>
                  {appointment.patient_unique_id && (
                    <>
                      <span className="text-gray-500 hidden sm:inline">•</span>
                      <p className="text-gray-400 text-xs sm:text-sm truncate">
                        {appointment.patient_unique_id}
                      </p>
                    </>
                  )}
                  {date && (
                    <>
                      <span className="text-gray-500 hidden sm:inline">•</span>
                      <div className="flex items-center text-xs sm:text-sm text-gray-400">
                        <Calendar className="h-3 w-3 mr-1 flex-shrink-0" />
                        <span className="truncate">{date}</span>
                      </div>
                    </>
                  )}
                  {time && (
                    <>
                      <span className="text-gray-500 hidden sm:inline">•</span>
                      <div className="flex items-center text-xs sm:text-sm text-gray-400">
                        <Clock className="h-3 w-3 mr-1 flex-shrink-0" />
                        <span className="truncate">{time}</span>
                      </div>
                    </>
                  )}
                  {bookedByText && (
                    <>
                      <span className="text-gray-500 hidden sm:inline">•</span>
                      <p className={`text-xs sm:text-sm truncate ${
                        isPatientBooked ? 'text-blue-300' : 'text-gray-400'
                      }`}>
                        Booked by: {bookedByText}
                      </p>
                    </>
                  )}
                </div>
                <div className="flex gap-1 sm:gap-2 flex-shrink-0">
                  {appointment.status === 'Submitted' ? (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleViewPDF(appointment)}
                        className="bg-blue-600/20 border-blue-500 text-blue-300 hover:bg-blue-600/30 text-xs px-1.5 sm:px-2 py-1 h-7 w-7 sm:w-auto"
                        title="View"
                      >
                        <Eye className="h-3 w-3 sm:mr-1" />
                        <span className="hidden sm:inline">View</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handlePrintAppointment(appointment)}
                        className="bg-green-600/20 border-green-500 text-green-300 hover:bg-green-600/30 text-xs px-1.5 sm:px-2 py-1 h-7 w-7 sm:w-auto"
                        title="Print"
                      >
                        <Download className="h-3 w-3 sm:mr-1" />
                        <span className="hidden sm:inline">Print</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEmailAppointment(appointment)}
                        className="bg-purple-600/20 border-purple-500 text-purple-300 hover:bg-purple-600/30 text-xs px-1.5 sm:px-2 py-1 h-7 w-7 sm:w-auto"
                        title="Email"
                      >
                        <Mail className="h-3 w-3 sm:mr-1" />
                        <span className="hidden sm:inline">Email</span>
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEditAppointment(appointment)}
                        className="bg-purple-600/20 border-purple-500 text-purple-300 hover:bg-purple-600/30 text-xs px-1.5 sm:px-2 py-1 h-7 w-7 sm:w-auto"
                        title="Edit"
                      >
                        <Edit className="h-3 w-3 sm:mr-1" />
                        <span className="hidden sm:inline">Edit</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleViewMedicalHistory(appointment)}
                        className="bg-blue-600/20 border-blue-500 text-blue-300 hover:bg-blue-600/30 text-xs px-1.5 sm:px-2 py-1 h-7 w-7 sm:w-auto"
                        title="Medical History"
                      >
                        <FileText className="h-3 w-3 sm:mr-1" />
                        <span className="hidden sm:inline">History</span>
                      </Button>
                    </>
                  )}
                </div>
              </div>
              );
            });
            })()}
          </div>
          
          {/* Pagination Controls for List View */}
          {(() => {
            const totalPages = Math.ceil(appointments.length / itemsPerPage);
            if (appointments.length <= itemsPerPage) return null;
            
            return (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-gray-700 mt-4">
                <div className="text-sm text-gray-400">
                  Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, appointments.length)} of {appointments.length} appointments
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Previous
                  </Button>
                  <div className="flex items-center space-x-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                      if (
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 1 && page <= currentPage + 1)
                      ) {
                        return (
                          <Button
                            key={page}
                            variant={currentPage === page ? "default" : "outline"}
                            size="sm"
                            onClick={() => setCurrentPage(page)}
                            className={
                              currentPage === page
                                ? "bg-blue-600 text-white hover:bg-blue-700"
                                : "border-gray-600 text-gray-300 hover:bg-gray-700"
                            }
                          >
                            {page}
                          </Button>
                        );
                      } else if (
                        page === currentPage - 2 ||
                        page === currentPage + 2
                      ) {
                        return (
                          <span key={page} className="text-gray-500 px-2">
                            ...
                          </span>
                        );
                      }
                      return null;
                    })}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage >= totalPages}
                    className="border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            );
          })()}
        </>
      ) : (
        /* Detailed View (Card Grid) with Pagination */
        <>
        <div className="space-y-4">
          {(() => {
            const totalPages = Math.ceil(appointments.length / itemsPerPage);
            const startIndex = (currentPage - 1) * itemsPerPage;
            const endIndex = startIndex + itemsPerPage;
            const paginatedAppointments = appointments.slice(startIndex, endIndex);

            return paginatedAppointments.map((appointment) => {
            const { date, time } = formatDateTime(appointment.appointment_date, appointment.appointment_time);
            // Check if appointment was booked by patient
            const isPatientBooked = appointment.creator_type === 'patient' || appointment.creator_patient_name;
            // Format creator name with "Dr." prefix only if creator is a doctor (NOT a patient)
            const getBookedByText = () => {
              if (!appointment.creator_name && !appointment.creator_patient_name) return null;
              const creatorName = appointment.creator_name || appointment.creator_patient_name;
              
              // If it's a patient, don't add "Dr." prefix
              if (isPatientBooked) {
                return creatorName;
              }
              
              // For users, check if they are a doctor
              // creator_type can be 'doctor', 'admin', 'chief_doctor', 'staff', or 'user'
              // creator_role is the actual role from the users table
              const isDoctor = appointment.creator_type === 'doctor' || 
                              appointment.creator_role === 'doctor' ||
                              appointment.creator_type === 'chief_doctor' ||
                              (appointment.creator_type === 'user' && appointment.creator_role === 'doctor');
              
              if (isDoctor) {
                return `Dr. ${creatorName}`;
              }
              
              // For other roles (admin, staff, etc.), don't add "Dr."
              return creatorName;
            };
            const bookedByText = getBookedByText();
            
            return (
              <Card 
                key={appointment.id} 
                className={`glass-effect ${
                  isPatientBooked 
                    ? 'border-blue-500/50 bg-blue-900/30' 
                    : 'border-gray-700'
                }`}
              >
                <CardContent className="p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex-1 space-y-3">
                      {/* Header with Status */}
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center space-x-2 flex-wrap">
                          {getStatusIcon(appointment.status)}
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(appointment.status)}`}>
                            {appointment.status}
                          </span>
                          {appointment.status === 'Submitted' && (
                            <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
                              Completed
                            </span>
                          )}
                          {appointment.status === 'Submitted' && getSubmittedDateBadge(appointment.appointment_date)}
                          {appointment.status !== 'Submitted' && getTimeStatusBadge(appointment.time_status)}
                        </div>
                      </div>

                      {/* Patient Info */}
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2 flex-wrap">
                          <User className="h-4 w-4 text-gray-400" />
                          <span className="text-white font-semibold">{appointment.patient_name || 'N/A'}</span>
                          {appointment.patient_unique_id && (
                            <span className="text-gray-400 text-sm">({appointment.patient_unique_id})</span>
                          )}
                          {(appointment.appointment_reference_id || appointment.id) && (
                            <span className="text-blue-400 text-xs font-mono bg-blue-500/20 px-2 py-0.5 rounded">
                              {appointment.appointment_reference_id || `ID: ${appointment.id}`}
                            </span>
                          )}
                        </div>
                        
                        {appointment.patient_phone && (
                          <div className="flex items-center space-x-2">
                            <Phone className="h-4 w-4 text-gray-400" />
                            <span className="text-gray-300 text-sm">{appointment.patient_phone}</span>
                          </div>
                        )}
                        
                        {/* Booked By */}
                        {bookedByText && (
                          <div className="flex items-center space-x-2">
                            <User className="h-4 w-4 text-gray-400" />
                            <span className="text-gray-400 text-xs">Booked by:</span>
                            <span className={`text-sm ${
                              isPatientBooked 
                                ? 'text-blue-300' 
                                : 'text-gray-300'
                            }`}>
                              {bookedByText}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Appointment Details */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="flex items-center space-x-2">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          <div>
                            <p className="text-gray-400 text-xs">Date</p>
                            <p className="text-white text-sm">{date}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <Clock className="h-4 w-4 text-gray-400" />
                          <div>
                            <p className="text-gray-400 text-xs">Time</p>
                            <p className="text-white text-sm">{time}</p>
                          </div>
                        </div>
                      </div>

                      {/* Service Type & Doctor */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {appointment.service_type && (
                          <div>
                            <p className="text-gray-400 text-xs">Service</p>
                            <p className="text-white text-sm">{appointment.service_type}</p>
                          </div>
                        )}
                        
                        {appointment.doctor_name && (
                          <div className="flex items-center space-x-2">
                            <Stethoscope className="h-4 w-4 text-gray-400" />
                            <div>
                              <p className="text-gray-400 text-xs">Doctor</p>
                              <p className="text-white text-sm">{appointment.doctor_name}</p>
                              {appointment.specialization && (
                                <p className="text-gray-400 text-xs">{appointment.specialization}</p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Notes */}
                      {appointment.notes && (
                        <div>
                          <p className="text-gray-400 text-xs mb-1">Notes</p>
                          <p className="text-gray-300 text-sm">{appointment.notes}</p>
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-3 mt-4">
                      {appointment.status === 'Submitted' ? (
                        // For submitted appointments, show View, Print, and Email buttons (same as Patient Submissions)
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleViewPDF(appointment)}
                            className="flex-1 bg-blue-600/20 border-blue-500 text-blue-300 hover:bg-blue-600/30 text-xs"
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            View
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handlePrintAppointment(appointment)}
                            className="flex-1 bg-green-600/20 border-green-500 text-green-300 hover:bg-green-600/30 text-xs"
                          >
                            <Download className="h-3 w-3 mr-1" />
                            Print
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEmailAppointment(appointment)}
                            className="flex-1 bg-purple-600/20 border-purple-500 text-purple-300 hover:bg-purple-600/30 text-xs"
                          >
                            <Mail className="h-3 w-3 mr-1" />
                            Email
                          </Button>
                        </>
                      ) : (
                        // For non-submitted appointments, show Edit and Medical History buttons
                        <>
                          <Button
                            onClick={() => handleEditAppointment(appointment)}
                            className="flex-1 edit-gradient text-white hover:opacity-90 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2.5"
                          >
                            <Edit className="h-4 w-4 flex-shrink-0" />
                            <span>Edit</span>
                          </Button>
                          <Button
                            onClick={() => handleViewMedicalHistory(appointment)}
                            className="flex-1 medical-gradient text-white hover:opacity-90 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2.5"
                          >
                            <FileText className="h-4 w-4 flex-shrink-0" />
                            <span className="hidden sm:inline">Medical History</span>
                            <span className="sm:hidden">History</span>
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          });
          })()}
        </div>
        
        {/* Pagination Controls for Detailed View */}
        {(() => {
          const totalPages = Math.ceil(appointments.length / itemsPerPage);
          if (appointments.length <= itemsPerPage) return null;
          
          return (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-gray-700 mt-4">
              <div className="text-sm text-gray-400">
                Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, appointments.length)} of {appointments.length} appointments
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <div className="flex items-center space-x-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                    if (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    ) {
                      return (
                        <Button
                          key={page}
                          variant={currentPage === page ? "default" : "outline"}
                          size="sm"
                          onClick={() => setCurrentPage(page)}
                          className={
                            currentPage === page
                              ? "bg-blue-600 text-white hover:bg-blue-700"
                              : "border-gray-600 text-gray-300 hover:bg-gray-700"
                          }
                        >
                          {page}
                        </Button>
                      );
                    } else if (
                      page === currentPage - 2 ||
                      page === currentPage + 2
                    ) {
                      return (
                        <span key={page} className="text-gray-500 px-2">
                          ...
                        </span>
                      );
                    }
                    return null;
                  })}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage >= totalPages}
                  className="border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          );
        })()}
        </>
      )}
        </CardContent>
      </Card>

      {/* Edit Appointment Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="glass-effect border-gray-700 bg-gray-900/95 backdrop-blur-sm max-w-2xl w-[95vw]">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Appointment</DialogTitle>
            <DialogDescription className="text-gray-400">
              Update appointment details
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-date" className="text-white">Date *</Label>
                <Input
                  id="edit-date"
                  type="date"
                  value={editFormData.appointment_date}
                  onChange={(e) => {
                    setEditFormData(prev => ({ ...prev, appointment_date: e.target.value, time_slot: '' }));
                    // Load booked slots when date changes
                    if (e.target.value) {
                      const currentAppointmentTime = editingAppointment?.appointment_time;
                      loadBookedSlots(e.target.value, null, currentAppointmentTime).then(slots => {
                        setEditBookedSlots(slots);
                      });
                    } else {
                      setEditBookedSlots([]);
                    }
                  }}
                  className="bg-gray-800 border-gray-600 text-white"
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit-service" className="text-white">Service Type</Label>
                <Input
                  id="edit-service"
                  type="text"
                  value={editFormData.service_type}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, service_type: e.target.value }))}
                  placeholder="e.g., Checkup, Cleaning"
                  className="bg-gray-800 border-gray-600 text-white"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="edit-time" className="text-white">Time Slot *</Label>
                <span className="text-sm text-green-400">
                  {getAvailableSlotsCount(editFormData.appointment_date, editBookedSlots)} slots available
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-64 overflow-y-auto p-2 bg-gray-800 rounded-lg border border-gray-600">
                {generateTimeSlots(editFormData.appointment_date, editBookedSlots).map((slot) => {
                  const isBooked = editBookedSlots.includes(slot.value);
                  const isSelected = editFormData.time_slot === slot.value;
                  const availableCount = getAvailableSlotsCount(editFormData.appointment_date, editBookedSlots);
                  const isDisabled = isBooked || availableCount === 0;
                  
                  return (
                    <button
                      key={slot.value}
                      type="button"
                      onClick={() => !isDisabled && setEditFormData(prev => ({ ...prev, time_slot: slot.value }))}
                      disabled={isDisabled}
                      className={`
                        px-3 py-2 rounded text-sm font-medium transition-colors
                        ${isSelected 
                          ? 'medical-gradient text-white border-2 border-blue-400' 
                          : isBooked
                          ? 'bg-gray-700 text-gray-500 cursor-not-allowed line-through'
                          : 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-600'
                        }
                        ${isDisabled ? 'opacity-50' : 'cursor-pointer'}
                      `}
                    >
                      {slot.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-status" className="text-white">Status *</Label>
                <Select value={editFormData.status} onValueChange={(value) => setEditFormData(prev => ({ ...prev, status: value }))}>
                  <SelectTrigger id="edit-status" className="bg-gray-800 border-gray-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Scheduled">Scheduled</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                    <SelectItem value="Cancelled">Cancelled</SelectItem>
                    <SelectItem value="No Show">No Show</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="edit-notes" className="text-white">Notes</Label>
              <Textarea
                id="edit-notes"
                value={editFormData.notes}
                onChange={(e) => setEditFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Additional notes..."
                className="bg-gray-800 border-gray-600 text-white"
                rows={4}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowEditModal(false)}
              className="bg-gray-800 border-gray-600 text-white hover:bg-gray-700"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateAppointment}
              className="medical-gradient text-white hover:opacity-90"
            >
              Update Appointment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Medical History Modal - Using PatientFormsWizard for consistent progress bar */}
      <PatientFormsWizard
        open={showMedicalHistory}
        onClose={() => {
          setShowMedicalHistory(false);
          setSelectedPatientForHistory(null);
          setSelectedAppointmentId(null);
        }}
        patientId={selectedPatientForHistory?.id}
        patientName={selectedPatientForHistory?.name}
        appointmentId={selectedAppointmentId}
        onComplete={(data) => {
          // Handle completion if needed
          console.log('Patient record completed:', data);
        }}
      />

      {/* View PDF Modal - For submitted appointments */}
      {showViewPDF && selectedAppointmentForPDF && (
        <Dialog open={showViewPDF} onOpenChange={(open) => {
          setShowViewPDF(open);
          if (!open) {
            setViewingPdfUrl(null); // Clear PDF URL when modal closes
          }
        }}>
          <DialogContent className="glass-effect border-gray-700 bg-gray-900/95 backdrop-blur-sm max-w-7xl w-[98vw] max-h-[95vh] p-0 flex flex-col [&>button]:hidden">
            <DialogHeader className="px-6 pt-6 pb-4 border-b border-gray-700">
              <div className="flex items-center justify-between">
                <DialogTitle className="text-white text-xl">
                  Patient Record and Invoice
                  {selectedAppointmentForPDF?.appointmentReferenceId && (
                    <span className="text-gray-400 text-sm ml-2">
                      - {selectedAppointmentForPDF.appointmentReferenceId}
                    </span>
                  )}
                </DialogTitle>
                <div className="flex items-center gap-2">
                  {viewingPdfUrl && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleDownloadPDF}
                      className="bg-green-600/20 border-green-500 text-green-300 hover:bg-green-600/30"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download PDF
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowViewPDF(false);
                      setViewingPdfUrl(null);
                    }}
                    className="bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </DialogHeader>
            
            {/* PDF Viewer - If PDF URL exists, show PDF */}
            {viewingPdfUrl ? (
              <div 
                className="flex-1 overflow-hidden flex flex-col"
                style={{ 
                  minHeight: 'calc(95vh - 120px)',
                  maxHeight: 'calc(95vh - 120px)',
                  height: 'calc(95vh - 120px)'
                }}
              >
                <iframe 
                  src={`${viewingPdfUrl}#toolbar=1&navpanes=1&scrollbar=1`}
                  className="w-full h-full border-0 flex-1"
                  title="Patient Record PDF"
                  style={{ 
                    minHeight: 'calc(95vh - 120px)',
                    width: '100%',
                    display: 'block'
                  }}
                  allow="fullscreen"
                />
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto px-6 py-4" id="pdf-content-wrapper">
                {loadingSubmissionData ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div>
                  </div>
                ) : submissionData ? (
                  <ReviewSubmitPage
                    patientId={submissionData.patient_id}
                    patientName={submissionData.patient_name}
                    appointmentId={submissionData.appointment_id || selectedAppointmentForPDF?.appointmentId || null}
                    medicalHistory={submissionData.medical_history}
                    dentalExamination={submissionData.dental_examination}
                    procedures={submissionData.procedures}
                    hideDialog={true}
                    onBack={null}
                    onSubmit={null}
                  />
                ) : (
                  <ReviewSubmitPage
                    patientId={selectedAppointmentForPDF.patientId}
                    patientName={selectedAppointmentForPDF.patientName}
                    appointmentId={selectedAppointmentForPDF.appointmentId}
                    medicalHistory={null}
                    dentalExamination={null}
                    procedures={null}
                    hideDialog={true}
                    onBack={null}
                    onSubmit={null}
                  />
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* Summary */}
      {appointments.length > 0 && (
        <Card className="glass-effect border-gray-700">
          <CardContent className="p-4">
            <p className="text-gray-400 text-sm text-center">
              Showing {appointments.length} appointment{appointments.length !== 1 ? 's' : ''}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Book Appointment Modal - For Doctor Dashboard and Clinic Login Dashboard */}
      {!viewAsAdmin && (profile?.role === 'doctor' || profile?.role === 'chief_doctor' || profile?.role === 'clinic_login') && (
        <Dialog open={showBookingModal} onOpenChange={setShowBookingModal}>
          <DialogContent className="sm:max-w-[600px] glass-effect border-gray-700 bg-gray-900/95 backdrop-blur-sm">
            <DialogHeader>
              <DialogTitle className="text-white text-2xl">Book Appointment</DialogTitle>
              <DialogDescription className="text-gray-400 mt-2">
                Book appointment for {selectedPatientForBooking?.full_name || selectedPatientForBooking?.name || 'selected patient'}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Row 1: Patient Name and Phone */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="booking-patient-name" className="text-white">Patient Name *</Label>
                  <Input
                    id="booking-patient-name"
                    value={appointmentData.patient_name}
                    onChange={(e) => setAppointmentData({ ...appointmentData, patient_name: e.target.value })}
                    className="bg-gray-800 border-gray-600 text-white"
                    placeholder="Full name"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="booking-phone" className="text-white">Phone *</Label>
                  <Input
                    id="booking-phone"
                    value={appointmentData.phone}
                    onChange={(e) => setAppointmentData({ ...appointmentData, phone: e.target.value })}
                    className="bg-gray-800 border-gray-600 text-white"
                    placeholder="e.g. 9876543210"
                    required
                  />
                </div>
              </div>

              {/* Row 2: Date and Service Type */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="booking-date" className="text-white">Date *</Label>
                  <Input
                    id="booking-date"
                    type="date"
                    value={appointmentData.date}
                    onChange={(e) => handleBookingDateChange(e.target.value)}
                    className="bg-gray-800 border-gray-600 text-white"
                    min={new Date().toISOString().split('T')[0]}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="booking-service" className="text-white">Service Type</Label>
                  <Select 
                    value={appointmentData.service_type} 
                    onValueChange={(value) => setAppointmentData({ ...appointmentData, service_type: value })}
                  >
                    <SelectTrigger id="booking-service" className="bg-gray-800 border-gray-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Checkup">Checkup</SelectItem>
                      <SelectItem value="Cleaning">Cleaning</SelectItem>
                      <SelectItem value="Filling">Filling</SelectItem>
                      <SelectItem value="Extraction">Extraction</SelectItem>
                      <SelectItem value="Root Canal">Root Canal</SelectItem>
                      <SelectItem value="Crown">Crown</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Time Slot */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label htmlFor="booking-time" className="text-white">Time Slot *</Label>
                  <span className="text-sm text-green-400">
                    {getAvailableSlotsCount()} slots available
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-64 overflow-y-auto p-2 bg-gray-800 rounded-lg border border-gray-600">
                  {generateTimeSlots().map((slot) => {
                    const isBooked = bookedSlots.includes(slot.value);
                    const isSelected = appointmentData.time_slot === slot.value;
                    const isDisabled = isBooked || getAvailableSlotsCount() === 0;
                    
                    return (
                      <button
                        key={slot.value}
                        type="button"
                        onClick={() => !isDisabled && setAppointmentData({ ...appointmentData, time_slot: slot.value })}
                        disabled={isDisabled}
                        className={`
                          px-3 py-2 rounded text-sm font-medium transition-colors
                          ${isSelected 
                            ? 'medical-gradient text-white border-2 border-blue-400' 
                            : isBooked
                            ? 'bg-gray-700 text-gray-500 cursor-not-allowed line-through'
                            : 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-600'
                          }
                          ${isDisabled ? 'opacity-50' : 'cursor-pointer'}
                        `}
                      >
                        {slot.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Row 3: Status */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="booking-status" className="text-white">Status</Label>
                  <Select 
                    value={appointmentData.status} 
                    onValueChange={(value) => setAppointmentData({ ...appointmentData, status: value })}
                  >
                    <SelectTrigger id="booking-status" className="bg-gray-800 border-gray-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Pending">Pending</SelectItem>
                      <SelectItem value="Confirmed">Confirmed</SelectItem>
                      <SelectItem value="Completed">Completed</SelectItem>
                      <SelectItem value="Cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  setShowBookingModal(false);
                  setSelectedPatientForBooking(null);
                  setAppointmentData({
                    patient_name: '',
                    phone: '',
                    date: '',
                    time_slot: '',
                    service_type: 'Checkup',
                    status: 'Pending'
                  });
                  setBookedSlots([]);
                }}
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateBooking}
                disabled={!appointmentData.date || !appointmentData.time_slot}
                className="medical-gradient text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Create Appointment
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default AppointmentsTab;

